import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAppStore } from '../store/appStore';
import { ArrowLeft } from 'lucide-react';
import IULProjectDetails from './projects/IULProjectDetails';
import SingpassProjectDetails from './projects/SingpassProjectDetails';
import FAPLProjectDetails from './projects/FAPLProjectDetails';
import AdobeProjectDetails from './projects/AdobeProjectDetails';

const ProjectTimeline = () => {
  const { setCurrentPage } = useAppStore();
  const [selectedProject, setSelectedProject] = useState(null);

  const projects = [
    {
      id: 'iul',
      name: 'IUL Product Development',
      subtitle: 'SPOCK Engine Powered',
      image: '/pic/ispeed/IUL.webp',
      color: 'from-yellow-500 to-orange-500',
      position: { x: '20%', y: '15%' },
      size: 'large',
      details: {
        challenges: [
          'Client\'s FIRST IUL product implementation',
          'IUL computational complexity: 10x higher than traditional UL products',
          'Formula configuration workload: 10x more extensive than UL products'
        ],
        highlights: [
          'Introduced insureMO high-performance computing engine SPOCK with client approval',
          'Configured GoalSeek and Illustration formulas: 200+ calculation items per month',
          'Implemented recursive algorithm: 200 fewer configuration layers compared to traditional UL approach'
        ],
        performance: {
          spock: { time: '6.02s', items: 212 },
          traditional: { time: '4.58s', items: 20 }
        },
        configuration: {
          spock: { days: 2, items: '200+' },
          traditional: { days: 12.5, items: 20 }
        }
      }
    },
    {
      id: 'singpass',
      name: 'Singpass Integration',
      subtitle: 'Government System Integration',
      image: '/pic/ispeed/Singpass.webp',
      color: 'from-green-500 to-teal-500',
      position: { x: '60%', y: '15%' },
      size: 'medium',
      details: {
        challenges: [
          'Integration with Singapore Government\'s National Digital Identity Platform',
          'Complex security requirements: FAPI 2.0 compliance and PKI encryption standards',
          'Handling sensitive government-verified data with strict privacy regulations',
          'Implementing JWT-based authentication with JWKS rotation'
        ],
        highlights: [
          'Seamlessly integrated MyInfo API for secure data retrieval',
          'Implemented pre-fill functionality reducing form completion time by 80%',
          'Achieved government-grade security with end-to-end encryption'
        ],
        security: [
          'End-to-end encryption using PKI and digital signatures',
          'User consent required before any data sharing',
          'No personal data stored - retrieved only upon user authentication',
          'Compliance with Singapore PDPA and global security standards'
        ],
        milestones: [
          {
            date: '2025-01',
            title: 'MyInfo v3 → v4 Upgrade',
            duration: '1 month development',
            description: 'Seamless upgrade with zero downtime',
            icon: '🚀'
          },
          {
            date: '2025-11',
            title: 'MyInfo v4 → v5 Upgrade',
            duration: '2 weeks development',
            description: 'Enhanced security features and performance',
            icon: '⚡'
          }
        ],
        images: [
          '/pic/singpass/myinfo_component.webp',
          '/pic/singpass/myinfo_jwks.webp',




          '/pic/singpass/myinfo_overview.webp',
          '/pic/singpass/myinfo_auth.webp'
        ]
      }
    },
    {
      id: 'fapl',
      name: 'FAPL Channel Integration',
      subtitle: 'Asynchronous Data Synchronization',
      image: '/pic/ispeed/FAPL.webp',
      color: 'from-blue-500 to-cyan-500',
      position: { x: '60%', y: '42.5%' },
      size: 'medium',
      details: {
        challenges: [
          'FAPL(FINANCIAL ALLIANCE PTE. LTD.) required manual login to iSpeed for each data query, inefficient process',
          'Needed to synchronize entire channel policy data in one batch operation',
          'Complex async processing with RabbitMQ and retry mechanism',
          'Field-level encryption required for sensitive data (Name, NRIC, DOB)'
        ],
        highlights: [
          'AI-assisted development: 50% faster, completed in 1 month',
          'Guided intern through development with comprehensive code review',
          'Designed robust async system with RabbitMQ + S3 architecture',
          'Implemented 3-tier retry mechanism with automatic email alerts',
          'Complete API documentation (POST /jobs, GET /jobs/{jobId})',
          'Field-level AES encryption for PII data'
        ]
      }
    },
    {
      id: 'adobe',
      name: 'Adobe Offline Signature',
      subtitle: 'Secure Signature Upgrade',
      image: '/pic/ispeed/Adobe.webp',
      color: 'from-red-500 to-pink-500',
      position: { x: '60%', y: '70%' },
      size: 'medium',
      details: {
        challenges: [
          'Insurance agents prefer evening client meetings, often conflicting with system maintenance windows',
          'Network instability or Adobe service failures prevent signature pages from loading',
          'Clients require follow-up visits next day, increasing costs and degrading experience',
          'Monthly signature failure reports occurring 2-3 weeks per month, impacting business efficiency'
        ],
        highlights: [
          'Designed and implemented offline signature solution: supports asynchronous email-based signature flow',
          'Clients confirm all signer email addresses within iSpeed system',
          'System sends signature emails sequentially to required signers',
          'Clients complete signatures via email links without requiring real-time online presence',
          'System automatically emails next signer in sequence, achieving process automation',
          'Supports both online and offline signature modes with flexible switching capability',
          'Eliminates monthly 2-3 week signature failure issues, significantly improving client satisfaction'
        ]
      }
    }
  ];

  const ProjectNode = ({ project }) => {
    const isLarge = project.size === 'large';
    
    return (
      <motion.div
        initial={{ opacity: 0, scale: 0 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.6, delay: 0.2 }}
        className="absolute cursor-pointer"
        style={{ 
          left: project.position.x, 
          top: project.position.y,
          transform: 'translate(-50%, -50%)'
        }}
        onClick={() => setSelectedProject(project)}
        whileHover={{ scale: 1.05 }}
      >
        <div className={`relative ${isLarge ? 'w-80 h-120' : 'w-64 h-42'}`}>
          {/* Enhanced glowing border effect */}
          <div className="absolute -inset-2 bg-gradient-to-br from-purple-400/40 to-purple-600/40 rounded-3xl blur-lg"></div>
          <div className="absolute -inset-1 bg-gradient-to-br from-purple-300/30 to-purple-500/30 rounded-2xl blur-md"></div>
          <div className="absolute inset-0 border-2 border-purple-400/60 rounded-2xl shadow-2xl shadow-purple-500/30"></div>
          
          {/* Image container */}
          <div className="relative w-full h-full bg-white/10 backdrop-blur-sm rounded-2xl overflow-hidden border border-purple-300/40">
            <img 
              src={project.image} 
              alt={project.name}
              className="w-full h-full object-cover"
            />
          </div>
          
          {isLarge && (
            <div className="absolute -top-3 -right-3 w-8 h-8 bg-yellow-400 rounded-full flex items-center justify-center shadow-lg">
              <span className="text-sm font-bold text-black">★</span>
            </div>
          )}
        </div>
        
        {/* Text labels with better positioning */}
        <div className={`absolute ${isLarge ? 'top-full mt-6' : 'top-full mt-4'} left-1/2 transform -translate-x-1/2 text-center whitespace-nowrap`}>
          <div className={`${isLarge ? 'text-xl' : 'text-base'} font-bold text-white drop-shadow-lg`}>
            {project.name}
          </div>
          <div className={`${isLarge ? 'text-sm' : 'text-xs'} text-gray-300 drop-shadow-md`}>
            {project.subtitle}
          </div>
        </div>
      </motion.div>
    );
  };

  const ProjectDetailModal = ({ project, onClose }) => (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="modal-overlay"
      onClick={onClose}
    >
      <motion.div
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.8, opacity: 0 }}
        className="modal-content"
        onClick={(e) => e.stopPropagation()}
      >
        <button className="close-btn" onClick={onClose}>×</button>
        
        <div className="text-center mb-8">
          <h2 className="text-3xl font-bold mb-2">{project.name}</h2>
          <p className="text-gray-300">{project.subtitle}</p>
        </div>

        {project.id === 'iul' && <IULProjectDetails project={project} />}

        {project.id === 'singpass' && <SingpassProjectDetails project={project} />}

        {project.id === 'fapl' && <FAPLProjectDetails project={project} />}

        {project.id === 'adobe' && <AdobeProjectDetails project={project} />}

        {project.id !== 'iul' && project.id !== 'singpass' && project.id !== 'fapl' && project.id !== 'adobe' && (
          <div className="text-center py-8">
            <div className="w-80 h-48 bg-white/10 rounded-xl mx-auto mb-4 overflow-hidden border border-purple-300/30">
              <img
                src={project.image}
                alt={project.name}
                className="w-full h-full object-cover"
              />
            </div>
            <p className="text-gray-300">Project details and achievements</p>
          </div>
        )}
      </motion.div>
    </motion.div>
  );

  return (
    <div className="page">
      {/* Back Button */}
      <motion.button
        initial={{ opacity: 0, x: -50 }}
        animate={{ opacity: 1, x: 0 }}
        className="fixed top-8 left-8 z-10 p-3 rounded-full bg-white/10 backdrop-blur-sm border border-white/20 hover:bg-white/20 transition-all"
        onClick={() => setCurrentPage('landing')}
      >
        <ArrowLeft className="w-6 h-6" />
      </motion.button>

      {/* Title */}
      <motion.div
        initial={{ opacity: 0, y: -50 }}
        animate={{ opacity: 1, y: 0 }}
        className="absolute top-8 left-1 transform -translate-x-1/2 text-center w-full"
      >
        <h1 className="text-4xl font-bold mb-2">iSpeed Project Achievements</h1>
        <p className="text-gray-300">Click nodes to view details</p>
      </motion.div>

      {/* Timeline Background */}
      <motion.div
        initial={{ scaleX: 0 }}
        animate={{ scaleX: 1 }}
        transition={{ duration: 1.5, delay: 0.5 }}
        className="absolute left-0 w-full h-0.5 bg-gradient-to-r from-transparent via-white/30 to-transparent"
        style={{ top: '50%' }}
      />

      {/* Project Nodes */}
      <div className="relative w-full h-full">
        {projects.map((project) => (
          <ProjectNode key={project.id} project={project} />
        ))}
      </div>

      {/* Connection Lines to Timeline */}
      <svg className="absolute inset-0 w-full h-full pointer-events-none" style={{ zIndex: 0 }} preserveAspectRatio="none" viewBox="0 0 100 100">
        {projects.map((project, index) => (
          <motion.line
            key={`line-${project.id}`}
            initial={{ pathLength: 0, opacity: 0 }}
            animate={{ pathLength: 1, opacity: 0.3 }}
            transition={{ duration: 0.8, delay: 1 + index * 0.2 }}
            x1={parseFloat(project.position.x)}
            y1={parseFloat(project.position.y)}
            x2={parseFloat(project.position.x)}
            y2={50}
            stroke="rgba(255,255,255,0.3)"
            strokeWidth="0.2"
            strokeDasharray="1,1"
            vectorEffect="non-scaling-stroke"
          />
        ))}
      </svg>

      {/* Detail Modal */}
      <AnimatePresence>
        {selectedProject && (
          <ProjectDetailModal
            project={selectedProject}
            onClose={() => setSelectedProject(null)}
          />
        )}
      </AnimatePresence>
    </div>
  );
};

export default ProjectTimeline;

// Custom scrollbar styles
const style = document.createElement('style');
style.textContent = `
  .custom-scrollbar::-webkit-scrollbar {
    width: 8px;
  }

  .custom-scrollbar::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.05);
    border-radius: 10px;
  }

  .custom-scrollbar::-webkit-scrollbar-thumb {
    background: rgba(139, 92, 246, 0.3);
    border-radius: 10px;
  }

  .custom-scrollbar::-webkit-scrollbar-thumb:hover {
    background: rgba(139, 92, 246, 0.5);
  }

  /* Firefox */
  .custom-scrollbar {
    scrollbar-width: thin;
    scrollbar-color: rgba(139, 92, 246, 0.3) rgba(255, 255, 255, 0.05);
  }
`;
if (!document.head.querySelector('style[data-scrollbar-style]')) {
  style.setAttribute('data-scrollbar-style', 'true');
  document.head.appendChild(style);
}

